import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { Box, Button, Grid, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import FormikTextField from '../../components/formik-input'
import BasicDialog from '../../components/m3/basic-dialog'
import OnboardingScreen from '../../components/screen/onboarding-screen'
import SelectedFolderBanner from '../../components/selected-folder-banner'
import { ActivationLocationState } from '../../providers/activation-provider'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'

import AgreementText from './agreement-text'
import useStyles from './index.styles'

interface FormValues {
  signature: string
}

const ParticipationAgreement = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const classes = useStyles
  const {
    fromDashboard,
    folderId: selectedFolderId,
    nodeId,
    nodeTypeName,
    meshifyId,
    sidewalkId,
  } = (useLocation().state || {}) as {
    fromDashboard: boolean
  } & ActivationLocationState

  const { handleLogout } = useContext(AuthenticationContext)

  const [loading, setLoading] = useState<boolean>(true)
  const [agreementText, setAgreementText] = useState<string>('')
  const [agreementError, setAgreementError] = useState<string>('')
  const [additionalFolders, setAdditionalFolders] = useState<IFolderType[]>([])
  const [dialogOpen, setDialogOpen] = useState<boolean>(false)
  const [folder, setFolder] = useState<IFolderType | undefined>(undefined)

  const signatureSchema = Yup.object().shape({
    signature: Yup.string().trim().required('Please enter your name'),
  })

  useEffect(() => {
    if (!selectedFolderId) navigate(-1) // Go back if no folder selected
  }, [selectedFolderId, navigate])

  const fetchAgreementData = useCallback(async () => {
    if (!selectedFolderId) {
      setLoading(false)
      return
    }

    setLoading(true)
    setAgreementError('')

    try {
      const response = await Api.get(`/api/v2/protect/participation-agreement/${selectedFolderId}`)
      if (response.ok) {
        if (response.body.latestAgreementAccepted) {
          // Already signed, navigate to the next screen in onboarding
          if (!fromDashboard) {
            navigate('/meet-sensor', { state: { folderId: selectedFolderId, nodeId, nodeTypeName, meshifyId, sidewalkId } }) // Pass along necessary state for sensor activation
            return
          }
        }
        const folderResponse = await Api.get(`/api/folders/${selectedFolderId}`)
        if (folderResponse.ok) {
          setFolder(folderResponse.body)
        } else enqueueSnackbar('Failed to fetch folder information')
        setAgreementText(response.body.agreementText)
        setAdditionalFolders(response.body.additionalFolders || [])
      } else {
        const apiSignature = response.body.details?.[0]?.value?.signature
        let errorMessage = 'Failed to fetch participation agreement'
        if (apiSignature === 'protect_not_found_missing_attributes') errorMessage = 'Participant folder attributes are missing.'
        else if (apiSignature === 'protect_not_found_agreement_not_found') errorMessage = 'Participation Agreement is missing.'
        else if (apiSignature === 'protect_precondition_failed_client_company_path_missing') errorMessage = 'ClientCompanyPath is missing.'
        else if (apiSignature === 'protect_precondition_failed_program_path_missing') errorMessage = 'ProgramPath is missing.'
        else if (apiSignature === 'protect_permission_denied_not_assigned_folder') errorMessage = 'You do not have permission to make changes to this account.'
        enqueueSnackbar(errorMessage)
        setAgreementError(errorMessage)
      }
    } catch {
      const errorMessage = 'Failed to fetch participation agreement'
      enqueueSnackbar(errorMessage)
      setAgreementError(errorMessage)
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar, fromDashboard, meshifyId, navigate, nodeId, nodeTypeName, selectedFolderId, sidewalkId])

  useEffect(() => {
    fetchAgreementData()
  }, [fetchAgreementData])

  const submitAgreement = async (values: FormValues) => {
    const { signature } = values
    const folderIds = new Set([selectedFolderId, ...additionalFolders.map((f) => f.id)])
    if (folderIds.size > 1) {
      navigate('/participation-agreement-multiple', {
        state: { additionalFolders, savedSignature: signature, fromDashboard, folderId: selectedFolderId, nodeId, nodeTypeName, meshifyId, sidewalkId },
      }) // Pass along necessary state for sensor activation
    } else {
      const response = await Api.post(`/api/v2/protect/participation-agreement`, {
        folderIds: [selectedFolderId],
        signedBy: signature,
      })
      if (response.ok) {
        if (fromDashboard) {
          const attributesResponse = await Api.get(`/api/v2/folders/${selectedFolderId}/attributes`)
          if (attributesResponse.ok && (attributesResponse.body as IFolderAttributes).stepContactInformationUser === 0) {
            navigate('/onboarding-contacts', { state: { folderId: selectedFolderId } })
          } else {
            navigate(`/dashboard/${selectedFolderId}/devices`)
          }
        } else {
          navigate('/meet-sensor', { state: { folderId: selectedFolderId, nodeId, nodeTypeName, meshifyId, sidewalkId } }) // Pass along necessary state for sensor activation
        }
      } else {
        const apiSignature = response.body.details?.[0].value?.signature
        if (apiSignature === 'protect_not_found_missing_attributes') enqueueSnackbar('Failed to update agreement. Participant folder attributes are missing.')
        else if (apiSignature === 'protect_not_found_agreement_not_found') enqueueSnackbar('Failed to update agreement. Participation Agreement is missing.')
        else if (apiSignature === 'protect_precondition_failed_client_company_path_missing')
          enqueueSnackbar('Failed to update agreement. ClientCompanyPath is missing.')
        else if (apiSignature === 'protect_precondition_failed_program_path_missing') enqueueSnackbar('Failed to update agreement. ProgramPath is missing.')
        else if (apiSignature === 'protect_permission_denied_not_assigned_folder')
          enqueueSnackbar('You do not have permission to make changes to this account.')
        else enqueueSnackbar('Failed to update agreement, please try again')
      }
    }
  }

  return (
    <OnboardingScreen loading={loading}>
      {agreementError ? (
        <Grid container={true} size={12} sx={classes.errorContainer}>
          <Typography variant="body1" color="text.secondary">
            {agreementError}
          </Typography>
          <Grid container={true} sx={classes.errorButtonRow}>
            <Button variant="contained" onClick={() => navigate('/dashboard?skipFolderSelection=true', { replace: true })}>
              Go to Dashboard
            </Button>
            <Button variant="text" onClick={handleLogout}>
              Sign Out
            </Button>
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container={true} sx={classes.stickyBanner}>
            <SelectedFolderBanner folder={folder ?? null} isActivation={!fromDashboard} />
          </Grid>

          <Grid container={true} sx={classes.outerContainer}>
            <Grid container={true} sx={classes.agreementContainer}>
              <Typography variant="h6">Participation Agreement</Typography>
              <AgreementText agreementContent={agreementText} />
            </Grid>

            <Formik initialValues={{ signature: '' }} onSubmit={submitAgreement} validationSchema={signatureSchema}>
              {({ isSubmitting, isValid, dirty }) => (
                <Form>
                  <Grid container={true} sx={classes.footerContainer}>
                    <Grid container={true} sx={classes.footerContent}>
                      <Box sx={classes.formInputContainer}>
                        <Typography variant="subtitle1">Signature</Typography>
                        <FormikTextField name="signature" label="Enter full name to sign" placeholder="Please enter full name" />
                      </Box>

                      <Grid container={true} sx={classes.signatureButtonRow}>
                        <Button variant="text" onClick={() => setDialogOpen(true)}>
                          Decline
                        </Button>

                        <Button type="submit" variant="contained" disabled={isSubmitting || !isValid || !dirty} loading={isSubmitting}>
                          Accept
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>

            <BasicDialog
              open={dialogOpen}
              onClose={() => setDialogOpen(false)}
              title="Decline Participation Agreement?"
              showCloseIcon
              textContent="You cannot proceed without accepting the participant agreement."
              confirmLabel="Sign Out"
              onConfirm={handleLogout}
              maxWidth="sm"
            />
          </Grid>
        </>
      )}
    </OnboardingScreen>
  )
}

export default ParticipationAgreement
